/**
 * Bounds the keypad feature owns. Nothing here is read from the RCU definition,
 * so a change to the device model cannot silently move a panel's address.
 */
export const KEYPAD_LIMITS = {
  /**
   * The reference app's own input-address ceiling: a panel answers on 1–64.
   * The firmware stores the value as a byte, so this is a configuration rule
   * rather than a protocol limit.
   */
  ADDRESS_MIN: 1,
  ADDRESS_MAX: 64,

  /** Longest name the console accepts, so the panel canvas can lay it out. */
  NAME_MAX: 40,
  /** Longest engraving label kept for a key. */
  KEY_NAME_MAX: 24,
  /** Longest free-text note saying where the panel is fitted. */
  LOCATION_MAX: 40,

  /**
   * Gap between the reads of one run.
   *
   * The firmware queues twenty messages, waits 100 ms and then drops, and every
   * mapping read is answered with a fresh publish that the 511-byte slot can
   * truncate. Reading one trigger at a time, spaced out, is what keeps a panel's
   * reads from being lost.
   */
  READ_SPACING_MS: 250,

  /**
   * How long to keep listening after the last read of a run before calling a
   * trigger "nothing stored".
   *
   * Each read is answered with its own publish, and the firmware gives no end
   * marker. Two spacings is long enough for an answer already on the wire to
   * arrive, and a late answer still wins over the absence this declares — see
   * `noteReading`.
   */
  READ_SETTLE_MS: 500,

  /** Keys any one panel can have: the widest layout in the catalog. */
  MAX_KEYS: 6,
} as const;

/**
 * CAT-6 branches the reference app offers an installer. The list is wiring
 * information for the fitter; nothing about it reaches the device.
 */
export const KEYPAD_CAT6_BRANCHES = [1, 3, 4, 5, 6, 7, 8] as const;

export type KeypadCat6Branch = (typeof KEYPAD_CAT6_BRANCHES)[number];
